<template>
  <aside
    ref="root"
    class="hero-facts"
    :class="{ 'hero-facts--light': variant === 'light', 'hero-facts--live': animated }"
    aria-label="Сравнение времени на ключевые этапы участия в тендере"
  >
    <p class="hero-facts__title">Время на решение по закупке</p>
    <p class="hero-facts__legend">
      <span class="hero-facts__legend-item hero-facts__legend-item--before">Без системы</span>
      <span class="hero-facts__legend-item hero-facts__legend-item--after">С AI-Tenders</span>
    </p>
    <ul class="hero-facts__list">
      <li v-for="row in rows" :key="row.label" class="hero-facts__row">
        <span class="hero-facts__label">{{ row.label }}</span>
        <div class="hero-facts__bars">
          <div class="hero-facts__track">
            <div
              class="hero-facts__fill hero-facts__fill--before"
              :style="{ width: row.beforePct + '%' }"
            />
            <span class="hero-facts__val hero-facts__val--before">{{ row.before }}</span>
          </div>
          <div class="hero-facts__track hero-facts__track--after">
            <div
              class="hero-facts__fill hero-facts__fill--after"
              :style="{ width: row.visualAfterPct + '%' }"
            />
            <span class="hero-facts__val hero-facts__val--after">{{ row.after }}</span>
          </div>
        </div>
        <p class="hero-facts__hint">{{ row.hint }}</p>
      </li>
    </ul>
    <p class="hero-facts__footnote">
      Длина синих полос увеличена для наглядности; подписи — фактические сроки.
      Ориентир по отделам 3–8 человек; фактические сроки зависят от объёма документации и регламентов.
    </p>
  </aside>
</template>

<script setup>
import { ref, onMounted, onUnmounted } from 'vue'
import { HERO_TIME_ROWS } from '@/landing/procurementTimeComparison'

defineProps({
  variant: {
    type: String,
    default: 'dark',
    validator: (v) => v === 'dark' || v === 'light',
  },
})

const rows = HERO_TIME_ROWS
const root = ref(null)
const animated = ref(false)
let io = null

onMounted(() => {
  if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
    animated.value = true
    return
  }
  const el = root.value
  if (!el) return
  io = new IntersectionObserver(
    ([entry]) => {
      if (entry.isIntersecting) {
        animated.value = true
        io?.disconnect()
      }
    },
    { threshold: 0.15 },
  )
  io.observe(el)
})

onUnmounted(() => io?.disconnect())
</script>

<style scoped>
.hero-facts {
  margin: 0;
  padding: 1.35rem 1.25rem;
  border-radius: 18px;
  background: rgba(255, 255, 255, 0.05);
  border: 1px solid rgba(255, 255, 255, 0.1);
  text-align: left;
}

.hero-facts__title {
  margin: 0 0 0.75rem;
  font-size: var(--text-sm, 0.875rem);
  font-weight: 600;
  color: #f5f5f7;
  text-align: center;
}

.hero-facts__legend {
  display: flex;
  gap: 1.25rem;
  margin: 0 0 1rem;
  font-size: var(--text-xs, 0.75rem);
  color: #86868b;
}

.hero-facts__legend-item--after {
  color: #7dc4ff;
}

.hero-facts__list {
  list-style: none;
  margin: 0;
  padding: 0;
  display: flex;
  flex-direction: column;
  gap: 1.15rem;
}

.hero-facts__label {
  display: block;
  font-size: var(--text-sm, 0.875rem);
  font-weight: 600;
  color: #e8e8ed;
  margin-bottom: 0.5rem;
  line-height: 1.35;
}

.hero-facts__bars {
  display: flex;
  flex-direction: column;
  gap: 0.4rem;
}

.hero-facts__track {
  position: relative;
  height: 2rem;
  background: rgba(255, 255, 255, 0.06);
  border-radius: 8px;
  overflow: hidden;
}

.hero-facts__fill {
  position: absolute;
  left: 0;
  top: 0;
  bottom: 0;
  border-radius: 8px;
  min-width: 4px;
  transform-origin: left center;
  transform: scaleX(0);
  transition: transform 0.75s cubic-bezier(0.22, 1, 0.36, 1);
}

.hero-facts--live .hero-facts__fill {
  transform: scaleX(1);
}

.hero-facts__fill--before {
  background: linear-gradient(90deg, #5a5a5e, #7a7a80);
}

.hero-facts__fill--after {
  background: linear-gradient(90deg, #0071e3, #2997ff);
  box-shadow: 0 0 12px rgba(41, 151, 255, 0.45);
}

.hero-facts__val {
  position: absolute;
  top: 50%;
  transform: translateY(-50%);
  font-size: var(--text-sm, 0.875rem);
  font-weight: 700;
  z-index: 2;
  line-height: 1.2;
  white-space: nowrap;
}

.hero-facts__val--before {
  left: 0.65rem;
  right: auto;
  color: #fff;
}

.hero-facts__val--after {
  right: 0.65rem;
  left: auto;
  color: #e8f4ff;
}

.hero-facts__hint {
  margin: 0.35rem 0 0;
  font-size: var(--text-xs, 0.75rem);
  line-height: 1.4;
  color: #86868b;
}

.hero-facts__footnote {
  margin: 1.1rem 0 0;
  font-size: var(--text-xs, 0.75rem);
  line-height: 1.45;
  color: #6e6e73;
}

.hero-facts--light {
  background: var(--surface-soft, #f8fafe);
  border: 1px solid var(--border-strong, #e8e8ed);
}

.hero-facts--light .hero-facts__title {
  color: var(--fg, #1d1d1f);
}

.hero-facts--light .hero-facts__legend {
  color: #6e6e73;
}

.hero-facts--light .hero-facts__legend-item--after {
  color: #0071e3;
}

.hero-facts--light .hero-facts__label {
  color: #1d1d1f;
}

.hero-facts--light .hero-facts__track {
  background: #e8e8ed;
  border: 1px solid #d2d2d7;
}

.hero-facts--light .hero-facts__fill--before {
  background: linear-gradient(90deg, #6e6e73, #86868b);
}

.hero-facts--light .hero-facts__fill--after {
  background: linear-gradient(90deg, #0071e3, #1a8fff);
  box-shadow: 0 0 10px rgba(0, 113, 227, 0.35);
}

.hero-facts--light .hero-facts__val--before {
  color: #fff;
}

.hero-facts--light .hero-facts__val--after {
  color: #003d82;
}

.hero-facts--light .hero-facts__hint {
  color: #6e6e73;
}

.hero-facts--light .hero-facts__footnote {
  color: #86868b;
}

@media (prefers-reduced-motion: reduce) {
  .hero-facts__fill {
    transition: none;
    transform: scaleX(1);
  }
}

</style>
